import { useEffect, useMemo, useState } from "react";
import Highcharts from "highcharts";
import { Checkbox, Col, Row } from "antd";
import { useSelector } from "react-redux";
import { trainingAPI } from "@api/TrainingAPI";
import { get_metric_info_from_identifier, getDropdownKey } from "@utils/chart_utils";
import { buildOptionGroup, extractPlayerOrPositionalData } from "@components/Nutrition/nutritionUtils";
import { OptionGroupSelect } from "@components/Compare/Components_1v1/OptionGroupSelect";
import BellCurveChart from "@components/Charts/BellCurveChart";
import { mapDataForTeamCognitionBellCurve } from "@mappers/model/cognitionBellCurveMapper";
import { LoadingComponent } from "@components/Common/LoadingComponent";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";

type Props = {
  statMetrics: any[];
  startDate: string;
  endDate: string;
};

const MAX_SUBJECTS = 15;

const getSubjectLabel = (key: string, allPlayers: any[], groups: any[]): string => {
  if (key === "full-team") return "Full Team";
  if (key.startsWith("player-")) {
    const id = parseInt(key.split("-")[1]);
    const p = allPlayers.find((pl: any) => pl.player_id === id);
    return p ? `${p.first_name} ${p.last_name}` : key;
  }
  if (key.startsWith("group-")) {
    const id = parseInt(key.split("-")[1]);
    const g = groups.find((gr: any) => gr.player_group_id === id);
    return g ? g.name : key;
  }
  if (key.startsWith("pos-")) return key.split("-").slice(1).join("-");
  return key;
};

const TeamCognitionBellCurveChart = ({ statMetrics, startDate, endDate }: Props) => {
  const {
    all_metric_types,
    allPlayers,
    positions,
    playerGroups: groups,
  } = useSelector(({ team }: any) => team);

  const [selectedSubjects, setSelectedSubjects] = useState<string[]>(["full-team"]);
  const [seriesDataByMetric, setSeriesDataByMetric] = useState<Record<number, any[]>>({});
  const [loading, setLoading] = useState(false);
  const [showCurveByMetric, setShowCurveByMetric] = useState<Record<number, boolean>>({});
  const [checkboxLeftByMetric, setCheckboxLeftByMetric] = useState<Record<number, number>>({});

  const allMetrics: any[] = useMemo(
    () => Object.values(all_metric_types as Record<string, any[]>).flat(),
    [all_metric_types]
  );

  const subjectOptions = useMemo(() => {
    const opts: any[] = [
      {
        label: <span>Team</span>,
        title: "Team",
        options: [{ label: <span>Full Team</span>, value: "full-team", key: "full-team" }],
      },
    ];
    if (allPlayers?.length) opts.push(buildOptionGroup("Players", allPlayers, "player"));
    if (positions?.length) opts.push(buildOptionGroup("Positions", positions, "pos"));
    if (groups?.length) opts.push(buildOptionGroup("Groups", groups, "group"));
    return opts;
  }, [allPlayers, positions, groups]);

  const handleSubjectChange = (value: string[]) => {
    if (value.length > MAX_SUBJECTS) {
      message.warning(`You can't select more than ${MAX_SUBJECTS} subjects.`);
      return;
    }
    setSelectedSubjects(value.length ? value : ["full-team"]);
  };

  const getShowCurve = (idx: number) =>
    showCurveByMetric[idx] !== false;

  const toggleShowCurve = (idx: number, checked: boolean) => {
    setShowCurveByMetric((prev) => ({ ...prev, [idx]: checked }));
  };

  const validMetrics = statMetrics.filter(Boolean);

  const handleChartCreated = (idx: number) => (chart: Highcharts.Chart) => {
    if (chart?.plotLeft) {
      const newLeft = chart.plotLeft - 6;
      setCheckboxLeftByMetric((prev) => {
        if (prev[idx] === newLeft) return prev;
        return { ...prev, [idx]: newLeft };
      });
    }
  };

  const optionsByMetric = useMemo(() => {
    const result: Record<number, Highcharts.Options | null> = {};
    validMetrics.forEach((_: string, idx: number) => {
      const subjects = seriesDataByMetric[idx] ?? [];
      const metric = subjects[0]?.metric ?? null;
      const showCurve = showCurveByMetric[idx] !== false;
      result[idx] = metric ? mapDataForTeamCognitionBellCurve({ subjects, metric, showCurve }) : null;
    });
    return result;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seriesDataByMetric, showCurveByMetric]);

  useEffect(() => {
    if (!validMetrics.length || !selectedSubjects.length) {
      setSeriesDataByMetric({});
      return;
    }

    const fetchAll = async () => {
      setLoading(true);
      try {
        const calls = validMetrics.flatMap((metricIdentifier: string, metricIdx: number) =>
          selectedSubjects.map(async (subjectKey: string) => {
            const { metric_id, metric_stat } = get_metric_info_from_identifier(metricIdentifier);
            const metric = allMetrics.find((m: any) => getDropdownKey(m, "metricId") === metric_id);
            const params = extractPlayerOrPositionalData(subjectKey);
            const data = await trainingAPI.getTestingData({
              start_date: startDate,
              end_date: endDate,
              metric_type: "cognition",
              stats: metric_stat,
              metric_id,
              ...params,
              do_not_aggregate: true,
              skip_color: true,
            });
            return { metricIdx, metric, data, stat: metric_stat, subjectKey };
          })
        );

        const results = await Promise.all(calls);

        const byMetric: Record<number, any[]> = {};
        results.forEach(({ metricIdx, metric, data, stat, subjectKey }) => {
          if (!metric) return;
          const label = getSubjectLabel(subjectKey, allPlayers ?? [], groups ?? []);
          byMetric[metricIdx] = byMetric[metricIdx] ?? [];
          byMetric[metricIdx].push({ label, metric, data, stat });
        });
        setSeriesDataByMetric(byMetric);
      } catch {
        message.error("Error loading cognition chart data.");
        setSeriesDataByMetric({});
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [
    validMetrics.join(","),
    selectedSubjects.join(","),
    startDate,
    endDate,
  ]);

  if (!validMetrics.length) return null;

  return (
    <div style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 24 }}>
      <div style={{ width: "80%", marginBottom: 16 }} className="player-group-pos-container">
        <OptionGroupSelect
          className="player-group-pos-left"
          selectedOptionVal={selectedSubjects}
          options={subjectOptions}
          placeholder="Full Team"
          width={500}
          sideId=""
          handleOptionGroupChange={handleSubjectChange}
        />
      </div>
      <LoadingComponent loading={loading} title="Loading chart data...">
        <Row gutter={[16, 16]} style={{ width: "90%" }}>
          {validMetrics.map((_: string, idx: number) => {
            const showCurve = getShowCurve(idx);
            const options = optionsByMetric[idx] ?? null;
            return (
              <Col span={12} key={idx}>
                <div style={{ position: "relative" }}>
                  <BellCurveChart options={options} onChartCreated={handleChartCreated(idx)} />
                  {options && (
                    <div
                      style={{
                        position: "absolute",
                        top: "10px",
                        left: `${checkboxLeftByMetric[idx] ?? 35}px`,
                        backgroundColor: "rgba(255, 255, 255, 0.7)",
                        borderRadius: 3,
                        padding: "2px 6px",
                        zIndex: 10,
                      }}
                    >
                      <Checkbox
                        checked={showCurve}
                        onChange={(e) => toggleShowCurve(idx, e.target.checked)}
                        style={{ color: "#888888" }}
                      >
                        Show Curve
                      </Checkbox>
                    </div>
                  )}
                </div>
              </Col>
            );
          })}
        </Row>
      </LoadingComponent>
    </div>
  );
};

export default TeamCognitionBellCurveChart;
